/**
 * @fileoverview Textarea widget — multi-line text.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <textarea> with rows, placeholder, minLength, maxLength.
 *
 * @component TextareaField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

export function TextareaField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { placeholder?: string; rows?: number; minLength?: number; maxLength?: number };
  const v = value ?? '';
  const exceedsMax = extras.maxLength != null && v.length > extras.maxLength;
  const belowMin = extras.minLength != null && v.length > 0 && v.length < extras.minLength;
  const computedError = errorMessage
    ?? (exceedsMax ? `Max length ${extras.maxLength}` : undefined)
    ?? (belowMin ? `Min length ${extras.minLength}` : undefined);
  const isInvalid = !!computedError || (!!requiredUnmet && !!props.requiredImmediate);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="textarea"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <textarea
        id={field.id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={extras.placeholder}
        rows={extras.rows ?? 3}
        minLength={extras.minLength}
        maxLength={extras.maxLength}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={isInvalid}
        className={`w-full rounded-xl px-3 py-2 text-sm outline-hidden focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 resize-none disabled:opacity-50 ${isInvalid ? 'border border-red-500 bg-red-50/50 text-red-900 ring-2 ring-red-500/20' : 'border border-slate-300 bg-white text-slate-900'}`}
      />
      {extras.maxLength && (
        <p className="text-[10px] text-slate-400 mt-0.5">{(value ?? '').length} / {extras.maxLength}</p>
      )}
    </FieldWrapper>
  );
}
